import { computed, ref } from 'vue'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { useAccountStore } from '@/stores/account'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'
import { unwrap } from '@/lib/queryFns'
import { patchThreadCache, removeThreadFromLists, upsertThreadCache } from '@/lib/threadCache'
import type { Thread, ThreadStatus } from '@/types/server'

type InfiniteThreadData = {
  pages: Array<{ threads: Thread[]; pagination: { cursor: string | null } }>
  pageParams: Array<string | undefined>
}

export function useThreadListQuery(status: () => ThreadStatus | undefined) {
  const accountStore = useAccountStore()
  const accountId = computed(() => accountStore.accountId)

  // Sent as `refresh` on the query's very first fetch (covers a page load/reload) and again
  // whenever requestRefresh() runs (the inbox's Refresh button) — the backend treats a present
  // `refresh` value as a signal to wake the IMAP/JMAP IDLE listener for the account, on top of
  // whatever TanStack Query itself decides to (re)fetch. Consumed after one fetch so routine
  // refetches (pagination, window focus, mutation-triggered invalidation) don't keep re-sending it.
  const pendingRefresh = ref<string | undefined>(new Date().toISOString())

  const query = useInfiniteQuery({
    queryKey: computed(() => queryKeys.threads.list(accountId.value!, status())),
    queryFn: async ({ pageParam }) => {
      const refresh = pendingRefresh.value
      pendingRefresh.value = undefined
      return unwrap(await api.listThreads(accountId.value!, {
        status: status(),
        cursor: pageParam,
        limit: 50,
        ...(refresh ? { refresh } : {}),
      }))
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.cursor ?? undefined,
    enabled: computed(() => !!accountId.value),
    // Automatic triggers (tab focus, app-switch focus, mount, reconnect) only refetch the thread
    // list if the cached data is older than 5 minutes. TanStack tracks the last successful fetch
    // (dataUpdatedAt) per query and gates these triggers against it, so page changes and focus
    // events coalesce to at most one background fetch per 5-minute window. The manual Refresh
    // button calls query.refetch() below, which force-fetches regardless and resets the window.
    staleTime: 5 * 60 * 1000,
  })

  function requestRefresh() {
    pendingRefresh.value = new Date().toISOString()
    return query.refetch()
  }

  const threads = computed<Thread[]>(() =>
    query.data.value?.pages.flatMap(p => p.threads).filter(t => {
      // Filter out sentinel/ghost threads: null lastSignalAt means signals were
      // reprocessed away; dates before Y2K are DB placeholders (epoch, etc.)
      if (!t.lastSignalAt) return false
      return new Date(t.lastSignalAt).getTime() > new Date('2000-01-01').getTime()
    }) ?? [],
  )

  const activeCount = computed(() => threads.value.length)
  const hasMore = computed(() => query.hasNextPage?.value ?? false)

  return { query, threads, activeCount, hasMore, requestRefresh }
}

export function useThreadDetailQuery(threadId: () => string | undefined) {
  const accountStore = useAccountStore()
  const accountId = computed(() => accountStore.accountId)

  const detailQuery = useQuery({
    queryKey: computed(() => queryKeys.threads.detail(accountId.value!, threadId()!)),
    queryFn: async () =>
      unwrap(await api.getThread(accountId.value!, threadId()!)),
    enabled: computed(() => !!accountId.value && !!threadId()),
  })

  const thread = computed(() => detailQuery.data.value)

  return { ...detailQuery, thread }
}

// Shared helper for status mutations (archive, move-to-inbox, delete)
function useThreadStatusMutation(targetStatus: ThreadStatus) {
  const queryClient = useQueryClient()
  const accountStore = useAccountStore()

  return useMutation({
    mutationFn: async (threadId: string) =>
      unwrap(await api.patchThread(accountStore.accountId!, threadId, { status: targetStatus })),
    onMutate: async (threadId) => {
      const accountId = accountStore.accountId!
      await queryClient.cancelQueries({ queryKey: queryKeys.threads.all(accountId) })
      const previous = queryClient.getQueriesData({ queryKey: queryKeys.threads.all(accountId) })
      // Optimistic removal from the source list
      queryClient.setQueriesData(
        { queryKey: queryKeys.threads.all(accountId) },
        (old: unknown) => {
          const data = old as { pages?: Array<{ threads: Thread[]; pagination: { cursor: string | null } }> } | undefined
          if (!data?.pages) return old
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              threads: page.threads.filter((t) => t.threadId !== threadId),
            })),
          }
        },
      )
      return { previous }
    },
    onError: (_err, _threadId, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data)
        }
      }
    },
    // Commit the server's response into the detail cache and every list it now belongs
    // in — so a status flip (e.g. undoing an archive) is reflected immediately, without
    // waiting for a refetch, even when the previous optimistic removal already dropped
    // the row from the list the user is looking at.
    onSuccess: (thread) => {
      const accountId = accountStore.accountId!
      upsertThreadCache(queryClient, accountId, thread)
    },
  })
}

// Shared helper for bulk status mutations
function useBulkThreadStatusMutation(targetStatus: ThreadStatus) {
  const queryClient = useQueryClient()
  const accountStore = useAccountStore()

  return useMutation({
    mutationFn: async (threadIds: string[]) => {
      const accountId = accountStore.accountId!
      await Promise.all(
        threadIds.map((id) => api.patchThread(accountId, id, { status: targetStatus }).then(unwrap)),
      )
    },
    onMutate: async (threadIds) => {
      const accountId = accountStore.accountId!
      const idsSet = new Set(threadIds)
      await queryClient.cancelQueries({ queryKey: queryKeys.threads.all(accountId) })
      const previous = queryClient.getQueriesData({ queryKey: queryKeys.threads.all(accountId) })
      queryClient.setQueriesData(
        { queryKey: queryKeys.threads.all(accountId) },
        (old: unknown) => {
          const data = old as { pages?: Array<{ threads: Thread[]; pagination: { cursor: string | null } }> } | undefined
          if (!data?.pages) return old
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              threads: page.threads.filter((t) => !idsSet.has(t.threadId)),
            })),
          }
        },
      )
      return { previous }
    },
    onError: (_err, _threadIds, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data)
        }
      }
    },
  })
}

export function useArchiveThread() {
  return useThreadStatusMutation('archived')
}

export function useMoveToInbox() {
  return useThreadStatusMutation('active')
}

export function useDeleteThread() {
  return useThreadStatusMutation('deleted')
}

export function useBulkArchive() {
  return useBulkThreadStatusMutation('archived')
}

export function useBulkMoveToInbox() {
  return useBulkThreadStatusMutation('active')
}

export function useBulkLabel() {
  const queryClient = useQueryClient()
  const accountStore = useAccountStore()

  return useMutation({
    mutationFn: async ({ threadIds, label, threads }: { threadIds: string[]; label: string; threads: Thread[] }) => {
      const accountId = accountStore.accountId!
      await Promise.all(
        threadIds.map((id) => {
          const existing = threads.find((t) => t.threadId === id)
          const currentLabels = existing?.labels ?? []
          const newLabels = currentLabels.includes(label) ? currentLabels : [...currentLabels, label]
          return api.patchThread(accountId, id, { labels: newLabels }).then(unwrap)
        }),
      )
    },
    onMutate: async ({ threadIds, label }) => {
      const accountId = accountStore.accountId!
      await queryClient.cancelQueries({ queryKey: queryKeys.threads.all(accountId) })
      const previous = queryClient.getQueriesData({ queryKey: queryKeys.threads.all(accountId) })
      // Optimistic update: add label to matching threads
      queryClient.setQueriesData(
        { queryKey: queryKeys.threads.all(accountId) },
        (old: unknown) => {
          const data = old as { pages?: Array<{ threads: Thread[]; pagination: { cursor: string | null } }> } | undefined
          if (!data?.pages) return old
          const idsSet = new Set(threadIds)
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              threads: page.threads.map((t) =>
                idsSet.has(t.threadId) && !t.labels.includes(label)
                  ? { ...t, labels: [...t.labels, label] }
                  : t,
              ),
            })),
          }
        },
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data)
        }
      }
    },
  })
}

export function useLabelThread() {
  const queryClient = useQueryClient()
  const accountStore = useAccountStore()

  return useMutation({
    mutationFn: async ({ threadId, labels }: { threadId: string; labels: string[] }) =>
      unwrap(await api.patchThread(accountStore.accountId!, threadId, { labels })),
    onMutate: async ({ threadId, labels }) => {
      const accountId = accountStore.accountId!
      const detailKey = queryKeys.threads.detail(accountId, threadId)
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previousDetail = queryClient.getQueryData<Thread>(detailKey)
      if (previousDetail) {
        queryClient.setQueryData<Thread>(detailKey, { ...previousDetail, labels })
      }
      return { previousDetail, detailKey }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(context.detailKey, context.previousDetail)
      }
    },
    // Commit the server's response (the authoritative Thread) instead of refetching it.
    onSuccess: (thread) => {
      const accountId = accountStore.accountId!
      patchThreadCache(queryClient, accountId, thread.threadId, thread)
    },
  })
}

export function useSnoozeThread() {
  const queryClient = useQueryClient()
  const accountStore = useAccountStore()

  return useMutation({
    // Snoozing archives the thread (leaves the active list) and sets the followup wake-up time.
    // Sending status explicitly keeps the client correct independent of backend defaulting.
    mutationFn: async ({ threadId, followupAt }: { threadId: string; followupAt: string }) =>
      unwrap(await api.patchThread(accountStore.accountId!, threadId, { status: 'archived', followupAt })),
    onMutate: async ({ threadId, followupAt }) => {
      const accountId = accountStore.accountId!
      const detailKey = queryKeys.threads.detail(accountId, threadId)
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previousDetail = queryClient.getQueryData<Thread>(detailKey)
      if (previousDetail) {
        queryClient.setQueryData<Thread>(detailKey, { ...previousDetail, status: 'archived', followupAt })
      }
      return { previousDetail, detailKey }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(context.detailKey, context.previousDetail)
      }
    },
    // Commit the server's response (the authoritative Thread) instead of refetching it.
    onSuccess: (thread) => {
      const accountId = accountStore.accountId!
      patchThreadCache(queryClient, accountId, thread.threadId, thread)
    },
  })
}

// Unsubscribing also archives the thread server-side (confirmed in the calling UI's
// copy), and the caller navigates away from the detail view on success — so there's no
// detail cache left to update; just drop the thread from whatever list has it, same as
// the other status mutations above.
export function useUnsubscribeThread() {
  const queryClient = useQueryClient()
  const accountStore = useAccountStore()

  return useMutation({
    mutationFn: async (threadId: string) =>
      unwrap(await api.unsubscribeThread(accountStore.accountId!, threadId)),
    onSuccess: (_data, threadId) => {
      const accountId = accountStore.accountId!
      removeThreadFromLists(queryClient, accountId, threadId)
    },
  })
}

export function usePrefetchActiveThreads() {
  const queryClient = useQueryClient()
  const accountStore = useAccountStore()

  async function prefetch() {
    const accountId = accountStore.accountId
    if (!accountId) return

    const pages: InfiniteThreadData['pages'] = []
    let cursor: string | undefined

    for (let i = 0; i < 3; i++) {
      const page = await unwrap(await api.listThreads(accountId, { status: 'active', limit: 50, cursor }))
      pages.push(page)
      cursor = page.pagination.cursor ?? undefined
      if (!cursor) break
    }

    queryClient.setQueryData<InfiniteThreadData>(
      queryKeys.threads.list(accountId, 'active'),
      { pages, pageParams: [undefined, ...pages.slice(0, -1).map(p => p.pagination.cursor ?? undefined)] },
    )
  }

  return { prefetch }
}
